import { useState, useEffect, useCallback } from 'react';

// Types matching the API response
interface TimeSeriesDataPoint {
  date: string;
  value: number;
}

interface AnalyticsSummary {
  totalConversations: number;
  totalReservations: number;
  conversationsTrend: number;
  reservationsTrend: number;
  period: string;
}

interface ConversationsByDay {
  data: TimeSeriesDataPoint[];
  total: number;
}

interface ReservationsByDay {
  data: TimeSeriesDataPoint[];
  total: number;
  successRate: number;
}

interface AnalyticsData {
  summary: AnalyticsSummary;
  conversationsByDay: ConversationsByDay;
  reservationsByDay: ReservationsByDay;
}

interface UseAnalyticsOptions {
  period?: '7d' | '30d' | '90d';
  venueId?: string;
  ownerId?: string;
}

interface UseAnalyticsReturn {
  data: AnalyticsData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAnalytics(options: UseAnalyticsOptions = {}): UseAnalyticsReturn {
  const { period = '30d', venueId, ownerId } = options;

  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ period });
      if (venueId) params.append('venueId', venueId);
      if (ownerId) params.append('ownerId', ownerId);

      const response = await fetch(`/api/analytics/summary?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch analytics: ${response.statusText}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch analytics';
      setError(message);
      console.error('[useAnalytics] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [period, venueId, ownerId]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    data,
    loading,
    error,
    refetch: fetchAnalytics,
  };
}

export type { AnalyticsData, AnalyticsSummary, TimeSeriesDataPoint };
